import React, { useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Icon, NewButton } from '../../atoms';
import { SidebarToggleButton } from '../../molecules';
import { DefaultLayout } from '../DefaultLayout';

import { SidebarProvider, SidebarContext } from '../../../providers';
import { userShape, spaceShape } from '../../../propsShape';

const Main = styled.main`
  overflow-y: auto;
  height: calc(100% - 77px);
  box-sizing: border-box;
`;

const Header = styled.header`
  display: flex;
  flex-shrink: 0;
  padding: 20px 16px;
  height: 77px;
  box-sizing: border-box;
  gap: 16px;
  align-items: center;
  border-bottom: 1px solid var(--border-muted);
`;

const TabButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TabbedLayout = ({
  currentOrganization,
  organizations,
  selectOrganization,
  user = null,
  users,
  groupedConversations,
  logout = null,
  tabs,
  spaces,
  children
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const mainRef = useRef(null);

  const navigationTabs = useMemo(
    () =>
      tabs.map(({ path, ...tab }) => ({
        ...tab,
        onClick: () => navigate(path)
      })),
    [tabs, navigate]
  );

  const activeTabIndex = useMemo(
    () => tabs.findIndex(({ path }) => path === pathname),
    [tabs, pathname]
  );

  return (
    <SidebarProvider>
      <DefaultLayout
        currentOrganization={currentOrganization}
        organizations={organizations}
        selectOrganization={selectOrganization}
        user={user}
        logout={logout}
        users={users}
        spaces={spaces}
        groupedConversations={groupedConversations}
      >
        <Header>
          <SidebarContext.Consumer>
            {({ sidebarCollapsed, setSidebarCollapsed }) =>
              sidebarCollapsed && (
                <SidebarToggleButton collapsed onClick={() => setSidebarCollapsed(false)} />
              )
            }
          </SidebarContext.Consumer>
          <TabButtonsContainer>
            {navigationTabs.map((tab, index) => (
              <NewButton
                key={index}
                tertiary
                active={activeTabIndex === index}
                onClick={tab.onClick}
              >
                <Icon size={24} displaySize={20} name={tab.icon} />
                {tab.title}
              </NewButton>
            ))}
          </TabButtonsContainer>
        </Header>

        <Main ref={mainRef}>{children}</Main>
      </DefaultLayout>
    </SidebarProvider>
  );
};

const organizationProps = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string
});

TabbedLayout.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      component: PropTypes.node.isRequired,
      title: PropTypes.string.isRequired,
      path: PropTypes.string,
      icon: PropTypes.string
    })
  ).isRequired,
  user: userShape,
  users: PropTypes.arrayOf(userShape).isRequired,
  groupedConversations: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayTitle: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  currentOrganization: organizationProps.isRequired,
  organizations: PropTypes.arrayOf(organizationProps).isRequired,
  selectOrganization: PropTypes.func.isRequired,
  logout: PropTypes.func,
  spaces: PropTypes.arrayOf(spaceShape).isRequired,
  children: PropTypes.node.isRequired
};

export { TabbedLayout };
